import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '', // Only used when editing
    name: '',
    role: 'admin'
  });
  const [permissions, setPermissions] = useState({
    canViewUsers: true,
    canCreateUsers: true,
    canEditUsers: true,
    canDeleteUsers: true,
    canViewLogs: true,
    canViewDashboard: true,
    canViewSettings: true,
    canManageAutomation: true
  });
  const [formErrors, setFormErrors] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/users`, getAuthHeaders());
      setUsers(response.data.users || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch users');
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchCurrentUser();
  }, [fetchUsers]);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
    } catch (err) {
      console.error('Failed to fetch current user:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    
    // Password only required when editing (optional for change)
    if (formData.password && formData.password.length > 0 && formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        // Update user
        const updateData = {
          email: formData.email,
          name: formData.name,
          role: formData.role
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        // Add permissions if editing admin user and current user is masteradmin
        if (formData.role === 'admin' && currentUser?.role === 'masteradmin') {
          updateData.permissions = permissions;
        }
        await axios.put(`${API_BASE_URL}/users/${editingUser._id}`, updateData, getAuthHeaders());
        setSuccess('User updated successfully!');
      } else {
        // Create user (without password - email will be sent for verification)
        await axios.post(`${API_BASE_URL}/users`, {
          email: formData.email,
          name: formData.name,
          role: formData.role
        }, getAuthHeaders());
        setSuccess('User created successfully! Verification email has been sent.');
      }
      
      setDialogVisible(false);
      resetForm();
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${editingUser ? 'update' : 'create'} user`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (user) => {
    confirmDialog({
      message: `Are you sure you want to delete user "${user.email}"? This action cannot be undone.`,
      header: 'Delete User',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await axios.delete(`${API_BASE_URL}/users/${user._id}`, getAuthHeaders());
          setSuccess('User deleted successfully!');
          fetchUsers();
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to delete user');
        }
      }
    });
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name || '',
      role: user.role || 'admin'
    });
    // Set permissions if user has them (admin users)
    if (user.permissions) {
      setPermissions(user.permissions);
    } else {
      // Default permissions
      setPermissions({
        canViewUsers: true,
        canCreateUsers: true,
        canEditUsers: true,
        canDeleteUsers: true,
        canViewLogs: true,
        canViewDashboard: true,
        canViewSettings: true,
        canManageAutomation: true
      });
    }
    setFormErrors({});
    setDialogVisible(true);
  };

  const handleAdd = () => {
    resetForm();
    setDialogVisible(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'admin'
    });
    setPermissions({
      canViewUsers: true,
      canCreateUsers: true,
      canEditUsers: true,
      canDeleteUsers: true,
      canViewLogs: true,
      canViewDashboard: true,
      canViewSettings: true,
      canManageAutomation: true
    });
    setFormErrors({});
    setEditingUser(null);
  };

  const handleCloseDialog = () => {
    setDialogVisible(false);
    resetForm();
    setError('');
  };

  const roleBodyTemplate = (rowData) => {
    const role = rowData.role || 'admin';
    const isMasterAdmin = role === 'masteradmin';
    
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.375rem 0.875rem',
        backgroundColor: isMasterAdmin ? '#fff3cd' : '#e3f2fd',
        color: isMasterAdmin ? '#856404' : '#1565c0',
        borderRadius: '16px',
        fontSize: '0.8125rem',
        fontWeight: '600',
        textTransform: 'capitalize',
        letterSpacing: '0.025em',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
      }}>
        {role === 'masteradmin' ? 'Master Admin' : 'Admin'}
      </span>
    );
  };

  const statusBodyTemplate = (rowData) => {
    const isVerified = rowData.emailVerified;
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.375rem 0.875rem',
        backgroundColor: isVerified ? '#d4edda' : '#fff3cd',
        color: isVerified ? '#155724' : '#856404',
        borderRadius: '16px',
        fontSize: '0.8125rem',
        fontWeight: '600',
        letterSpacing: '0.025em',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
      }}>
        {isVerified ? '✓ Verified' : '⚠ Pending'}
      </span>
    );
  };

  const handleResendVerification = async (user) => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.post(
        `${API_BASE_URL}/users/${user._id}/resend-verification`,
        {},
        getAuthHeaders()
      );
      setSuccess(`Verification email sent to ${response.data.email}`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  const actionsBodyTemplate = (rowData) => {
    const isNotVerified = !rowData.emailVerified;
    
    return (
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        {isNotVerified && (
          <Button
            icon="pi pi-send"
            className="p-button-rounded p-button-text p-button-sm p-button-warning"
            onClick={() => handleResendVerification(rowData)}
            tooltip="Resend Verification Email"
            tooltipOptions={{ position: 'top' }}
            disabled={loading}
          />
        )}
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleEdit(rowData)}
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          onClick={() => handleDelete(rowData)}
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };

  return (
    <div style={{ 
      padding: '2rem 2.5rem', 
      maxWidth: '1400px', 
      margin: '0 auto',
      minHeight: 'calc(100vh - 100px)'
    }}>
      <div style={{ 
        marginBottom: '2.5rem', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <div>
          <h1 style={{ 
            marginBottom: '0.5rem', 
            fontSize: '2rem', 
            fontWeight: '600',
            color: '#2c3e50'
          }}>
            Users
          </h1>
          <p style={{ 
            marginBottom: 0, 
            color: '#6c757d',
            fontSize: '0.95rem'
          }}>
            Manage admin users and their access
          </p>
        </div>
        <Button
          label="Add User"
          icon="pi pi-plus"
          iconPos="left"
          onClick={handleAdd}
          className="p-button-lg"
          style={{ 
            padding: '0.75rem 2rem',
            fontSize: '1rem',
            fontWeight: '500'
          }}
        />
      </div>

      {error && (
        <Message 
          severity="error" 
          text={error} 
          style={{ marginBottom: '1.5rem' }} 
          onClose={() => setError('')} 
        />
      )}

      {success && (
        <Message 
          severity="success" 
          text={success} 
          style={{ marginBottom: '1.5rem' }} 
          onClose={() => setSuccess('')} 
        />
      )}

      <Card style={{ 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '1.5rem'
      }}>
        <DataTable
          value={users}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 20, 50]}
          emptyMessage="No users found"
          style={{ width: '100%' }}
          className="p-datatable-sm"
        >
          <Column 
            field="name" 
            header="Name" 
            sortable 
            style={{ minWidth: '180px' }} 
          />
          <Column 
            field="email" 
            header="Email" 
            sortable 
            style={{ minWidth: '250px' }} 
          />
          <Column 
            field="role" 
            header="Role" 
            body={roleBodyTemplate} 
            sortable 
            style={{ minWidth: '140px' }} 
          />
          <Column 
            field="emailVerified" 
            header="Status" 
            body={statusBodyTemplate} 
            sortable 
            style={{ minWidth: '140px' }} 
          />
          <Column 
            field="createdAt" 
            header="Created" 
            sortable 
            style={{ minWidth: '160px' }}
            body={(rowData) => rowData.createdAt ? new Date(rowData.createdAt).toLocaleDateString() : '-'}
          />
          <Column 
            field="lastLogin" 
            header="Last Login" 
            sortable 
            style={{ minWidth: '180px' }}
            body={(rowData) => rowData.lastLogin ? new Date(rowData.lastLogin).toLocaleString() : 'Never'}
          />
          <Column 
            body={actionsBodyTemplate} 
            header="Actions" 
            style={{ minWidth: '180px', textAlign: 'center' }}
            exportable={false}
          />
        </DataTable>
      </Card>

      <Dialog
        header={
          <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0.5rem' }}>
            <i className="pi pi-user" style={{ marginRight: '0.75rem', fontSize: '1.25rem' }}></i>
            <span style={{ fontSize: '1.25rem', fontWeight: '600' }}>
              {editingUser ? 'Edit User' : 'Add New User'}
            </span>
          </div>
        }
        visible={dialogVisible}
        style={{ width: '90%', maxWidth: '500px' }}
        contentStyle={{ padding: '2rem 2.5rem' }}
        onHide={handleCloseDialog}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1.5rem 2.5rem' }}>
            <Button 
              label="Cancel" 
              icon="pi pi-times" 
              iconPos="left"
              onClick={handleCloseDialog} 
              className="p-button-text p-button-lg"
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem'
              }}
            />
            <Button
              label={editingUser ? 'Update' : 'Create'}
              icon={loading ? null : (editingUser ? "pi pi-check" : "pi pi-plus")}
              iconPos="left"
              onClick={handleSubmit}
              disabled={loading}
              loading={loading}
              className="p-button-lg"
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                minWidth: '140px'
              }}
            />
          </div>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="p-field" style={{ marginBottom: '1.75rem' }}>
            <label 
              htmlFor="name" 
              className="p-d-block" 
              style={{ 
                marginBottom: '0.75rem', 
                fontWeight: '600',
                color: '#495057',
                fontSize: '0.95rem'
              }}
            >
              Name *
            </label>
            <InputText
              id="name"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
              }}
              style={{ 
                width: '100%',
                padding: '0.75rem',
                fontSize: '1rem'
              }}
              placeholder="Enter full name"
              disabled={loading}
            />
            {formErrors.name && (
              <small className="p-error" style={{ display: 'block', marginTop: '0.5rem' }}>
                {formErrors.name}
              </small>
            )}
          </div>

          <div className="p-field" style={{ marginBottom: '1.75rem' }}>
            <label 
              htmlFor="email" 
              className="p-d-block" 
              style={{ 
                marginBottom: '0.75rem', 
                fontWeight: '600',
                color: '#495057',
                fontSize: '0.95rem'
              }}
            >
              Email *
            </label>
            <InputText
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (formErrors.email) setFormErrors({ ...formErrors, email: '' });
              }}
              style={{ 
                width: '100%',
                padding: '0.75rem',
                fontSize: '1rem'
              }}
              placeholder="Enter email address"
              disabled={loading || editingUser}
            />
            {editingUser && (
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
                Email cannot be changed
              </small>
            )}
            {formErrors.email && (
              <small className="p-error" style={{ display: 'block', marginTop: '0.5rem' }}>
                {formErrors.email}
              </small>
            )}
          </div>

          {editingUser && (
            <div className="p-field" style={{ marginBottom: '1.75rem' }}>
              <label 
                htmlFor="password" 
                className="p-d-block" 
                style={{ 
                  marginBottom: '0.75rem', 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}
              >
                Password (leave empty to keep current)
              </label>
              <Password
                id="password"
                value={formData.password}
                onChange={(e) => {
                  setFormData({ ...formData, password: e.target.value });
                  if (formErrors.password) setFormErrors({ ...formErrors, password: '' });
                }}
                style={{ width: '100%' }}
                inputStyle={{ 
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  height: 'auto',
                  lineHeight: '1.5'
                }}
                toggleMask
                disabled={loading}
                placeholder="Enter new password"
              />
              {formErrors.password && (
                <small className="p-error" style={{ display: 'block', marginTop: '0.5rem' }}>
                  {formErrors.password}
                </small>
              )}
            </div>
          )}
          
          {!editingUser && (
            <div style={{ 
              background: '#e7f3ff', 
              border: '1px solid #b3d9ff', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginBottom: '1.75rem' 
            }}>
              <p style={{ margin: 0, color: '#0056b3', fontSize: '0.9rem' }}>
                <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }}></i>
                A verification email will be sent to the user. They will need to verify their email and set a password before they can log in.
              </p>
            </div>
          )}

          {!editingUser && (
            <div className="p-field" style={{ marginBottom: '1.75rem' }}>
              <label 
                htmlFor="role" 
                className="p-d-block" 
                style={{ 
                  marginBottom: '0.75rem', 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}
              >
                Role *
              </label>
              <Dropdown
                id="role"
                value={formData.role}
                options={
                  currentUser?.role === 'masteradmin'
                    ? [
                        { label: 'Master Admin', value: 'masteradmin' },
                        { label: 'Admin', value: 'admin' }
                      ]
                    : [{ label: 'Admin', value: 'admin' }]
                }
                onChange={(e) => setFormData({ ...formData, role: e.value })}
                optionLabel="label"
                optionValue="value"
                placeholder="Select role"
                style={{ 
                  width: '100%'
                }}
                disabled={!currentUser}
              />
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d', fontSize: '0.85rem' }}>
                {currentUser?.role === 'masteradmin' 
                  ? 'You can create both Master Admin and Admin users'
                  : 'You can create Admin users only'}
              </small>
            </div>
          )}

          {editingUser && (
            <div className="p-field" style={{ marginBottom: '1.75rem' }}>
              <label 
                htmlFor="role" 
                className="p-d-block" 
                style={{ 
                  marginBottom: '0.75rem', 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}
              >
                Role *
              </label>
              <InputText
                id="role"
                value={editingUser.role === 'masteradmin' ? 'Master Admin' : 'Admin'}
                disabled
                style={{ 
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  backgroundColor: '#f8f9fa',
                  cursor: 'not-allowed'
                }}
              />
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d', fontSize: '0.85rem' }}>
                Role cannot be changed after user creation
              </small>
            </div>
          )}

          {/* Permissions Section - Only for masteradmin editing admin users */}
          {editingUser && editingUser.role === 'admin' && currentUser?.role === 'masteradmin' && (
            <div className="p-field" style={{ marginBottom: '1.75rem' }}>
              <label 
                className="p-d-block" 
                style={{ 
                  marginBottom: '1rem', 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}
              >
                Permissions
              </label>
              <div style={{ 
                background: '#f8f9fa', 
                border: '1px solid #dee2e6', 
                borderRadius: '8px', 
                padding: '1rem' 
              }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '0.75rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem', cursor: 'pointer' }}>
                      View Users
                    </label>
                    <InputSwitch
                      checked={permissions.canViewUsers}
                      onChange={(e) => setPermissions({ ...permissions, canViewUsers: e.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem', cursor: 'pointer' }}>
                      Create Users
                    </label>
                    <InputSwitch
                      checked={permissions.canCreateUsers}
                      onChange={(e) => setPermissions({ ...permissions, canCreateUsers: e.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem', cursor: 'pointer' }}>
                      Edit Users
                    </label>
                    <InputSwitch
                      checked={permissions.canEditUsers}
                      onChange={(e) => setPermissions({ ...permissions, canEditUsers: e.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem', cursor: 'pointer' }}>
                      Delete Users
                    </label>
                    <InputSwitch
                      checked={permissions.canDeleteUsers}
                      onChange={(e) => setPermissions({ ...permissions, canDeleteUsers: e.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem', cursor: 'pointer' }}>
                      View Logs
                    </label>
                    <InputSwitch
                      checked={permissions.canViewLogs}
                      onChange={(e) => setPermissions({ ...permissions, canViewLogs: e.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem', cursor: 'pointer' }}>
                      View Dashboard
                    </label>
                    <InputSwitch
                      checked={permissions.canViewDashboard}
                      onChange={(e) => setPermissions({ ...permissions, canViewDashboard: e.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem', cursor: 'pointer' }}>
                      View Settings
                    </label>
                    <InputSwitch
                      checked={permissions.canViewSettings}
                      onChange={(e) => setPermissions({ ...permissions, canViewSettings: e.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem', cursor: 'pointer' }}>
                      Manage Automation
                    </label>
                    <InputSwitch
                      checked={permissions.canManageAutomation}
                      onChange={(e) => setPermissions({ ...permissions, canManageAutomation: e.value })}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default Users;

